import { useNavigate } from "react-router"
import { motion } from "framer-motion"
import type { Variants } from "framer-motion"
import {
    Users,
    Maximize2,
    BedDouble,
    Clock,
    Star,
    Wifi,
    Projector,
    Snowflake,
    ShowerHead,
    Refrigerator,
    Tv,
    Coffee,
    Sparkles,
    Bath,
    Wine,
    ArrowRight,
    type LucideIcon,
} from "lucide-react"
import { cn } from "@/lib/utils"
import type { MockRoom } from "./mock-rooms"

const cardVariants: Variants = {
    hidden: { opacity: 0, y: 24 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
}

/** Chọn icon cho tiện nghi dựa theo từ khóa trong nhãn */
function amenityIcon(label: string): LucideIcon {
    const l = label.toLowerCase()
    if (l.includes("wifi")) return Wifi
    if (l.includes("máy chiếu") || l.includes("chiếu")) return Projector
    if (l.includes("điều hòa")) return Snowflake
    if (l.includes("sen")) return ShowerHead
    if (l.includes("tủ lạnh") || l.includes("minibar")) return Refrigerator
    if (l.includes("tv")) return Tv
    if (l.includes("trà") || l.includes("cà phê")) return Coffee
    if (l.includes("trang điểm")) return Sparkles
    if (l.includes("bồn tắm")) return Bath
    if (l.includes("wine") || l.includes("bar")) return Wine
    return Sparkles
}

/** Định dạng giá VND theo dấu chấm ngăn cách hàng nghìn */
function formatVnd(amount: number): string {
    return amount.toLocaleString("vi-VN")
}

const isVip = (type: MockRoom["type"]) => type === "vip" || type === "supervip"

interface RoomListCardProps {
    room: MockRoom
}

export function RoomListCard({ room }: RoomListCardProps) {
    const navigate = useNavigate()
    const vip = isVip(room.type)

    return (
        <motion.div
            variants={cardVariants}
            className="flex flex-col overflow-hidden rounded-2xl border border-black/5 bg-white shadow-[0_6px_24px_-12px_rgba(63,51,35,0.25)] transition-shadow hover:shadow-[0_12px_32px_-12px_rgba(63,51,35,0.35)] mt-6 hover:cursor-pointer"
        >
            {/* Ảnh + badge */}
            <div className="relative">
                <img
                    src={room.image}
                    alt={room.name}
                    loading="lazy"
                    className="h-52 w-full object-cover"
                />

                {/* Badge loại phòng — trái trên */}
                <span
                    className={cn(
                        "absolute left-3 top-3 inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-bold shadow-sm",
                        vip ? "bg-[#e0a63b] text-white" : "bg-white/95 text-[#4a4032]",
                    )}
                >
                    {vip && <Star className="size-3.5 fill-current" />}
                    {room.type === "supervip" ? "Super VIP" : vip ? "VIP" : "Standard"}
                </span>

                {/* Giá — phải trên */}
                <span className="absolute right-3 top-3 inline-flex items-baseline gap-0.5 rounded-md bg-[linear-gradient(135deg,#e6a93c_0%,#d98f2a_100%)] px-2 py-1 text-white shadow-sm">
                    <span className="text-xs font-bold">{formatVnd(room.pricePerSlot)}đ</span>
                    <span className="text-[9px] font-medium opacity-90">/slot</span>
                </span>
            </div>

            {/* Nội dung */}
            <div className="flex flex-1 flex-col p-4">
                {/* Tên + đánh giá */}
                <div className="flex items-start justify-between gap-2">
                    <h3 className="text-lg font-extrabold text-[#2B2B2B]">{room.name}</h3>
                    <span className="mt-0.5 inline-flex items-center gap-1 whitespace-nowrap text-sm font-semibold text-[#4a4032]">
                        <Star className="size-4 fill-[#f0b429] text-[#f0b429]" />
                        {room.rating.toFixed(1)}
                        <span className="font-normal text-[#9b8f7d]">({room.reviewCount})</span>
                    </span>
                </div>

                {/* Thông tin nhanh */}
                <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[13px] text-[#6f6555]">
                    <span className="inline-flex items-center gap-1.5">
                        <Users className="size-4 text-[#b9895c]" /> {room.guests} khách
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                        <Maximize2 className="size-4 text-[#b9895c]" /> {room.areaM2}m2
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                        <BedDouble className="size-4 text-[#b9895c]" /> {room.bed}
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                        <Clock className="size-4 text-[#b9895c]" /> {room.slotCount} khung giờ
                    </span>
                </div>

                {/* Tiện nghi */}
                <div className="mt-3 flex flex-wrap gap-2">
                    {room.amenities.map((a) => {
                        const Icon = amenityIcon(a)
                        return (
                            <span
                                key={a}
                                className="inline-flex items-center gap-1.5 rounded-lg border border-[#eceef2] bg-[#f9fafc] px-2.5 py-1 text-xs font-medium text-[#6f6555]"
                            >
                                <Icon className="size-3.5 text-[#b9895c]" />
                                {a}
                            </span>
                        )
                    })}
                </div>

                {/* Nút xem chi tiết */}
                <button
                    type="button"
                    onClick={() => navigate(`/phong-nghi/${room.id}`)}
                    className="mt-4 inline-flex w-full items-center justify-center gap-1.5 rounded-lg bg-[#f5f2ec] py-2.5 text-sm font-semibold text-[#6f6555] transition-colors hover:bg-[#8C7348] hover:text-white"
                >
                    Xem chi tiết
                    <ArrowRight className="size-4" />
                </button>
            </div>
        </motion.div>
    )
}
